import { useEffect, useRef, useState } from "react";

import {
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import type { IOptionItem } from "@/components/ui/dropdown/types";
import ModalHeader from "@/components/ui/modal/modalHeader";
import { DATA_SETTINGS_MESSAGE } from "@/constants/message";
import useAdmAreaMapping from "@/hooks/dataset-managment/useAdmAreaMapping";
import type { DataSettingPopupType, DataSettings } from "@/types/dataSetting";

import { Button } from "equator-design-system";

import AdmAreaMappingView from "./AdmAreaMappingView";
import DataSettingsHub from "./DataSettingsHub";
import SpatialSettingsView from "./SpatialSettingsView";

const TEST_ID_PREFIX = "dataset-management-data-settings";

type DataSettingsViewName = "hub" | "spatial" | "admArea";

type DataSettingsDialogProps = {
  datasetId?: string;
  countryId?: string;
  type: DataSettingPopupType;
  dataSetting: DataSettings | null;
  isSavingDataSetting: boolean;
  /** Increases once a save lands. A failed save leaves it where it was. */
  dataSettingSavedCount: number;
  /** True while another dialog is stacked on top. Only the topmost one draws a
   * backdrop; two would darken the page almost to black. */
  isBehindAnotherDialog: boolean;
  attributesLatitude: IOptionItem[];
  attributesLongitude: IOptionItem[];
  attributesForAdminArea: IOptionItem[];
  /** Name of the saved geometry column (`is_reference_geo`), if any. */
  spatialAttributeLabel?: string;
  isCoordinateRejected?: boolean;
  /** The dataset already carries a mapping, from an earlier session. */
  hasSavedAdmAreaMapping: boolean;
  isSavingCountry: boolean;
  setDataSetting: React.Dispatch<React.SetStateAction<DataSettings | null>>;
  onClose: () => void;
  onSaveDataSetting: () => void;
  onSaveCountry: (
    countryId: string,
    callbacks: { onSaved: () => void; onNotSaved: () => void }
  ) => void;
  /** A mapping was written, so the dataset has to be read again. */
  onMappingSaved: () => void;
};

/** Data Settings as a hub: spatial configuration and ADM Area mapping are
 * configured one at a time, each in its own view of this same dialog. */
const DataSettingsDialog = ({
  datasetId,
  countryId,
  type,
  dataSetting,
  isSavingDataSetting,
  dataSettingSavedCount,
  isBehindAnotherDialog,
  attributesLatitude,
  attributesLongitude,
  attributesForAdminArea,
  spatialAttributeLabel,
  isCoordinateRejected = false,
  hasSavedAdmAreaMapping,
  isSavingCountry,
  setDataSetting,
  onClose,
  onSaveDataSetting,
  onSaveCountry,
  onMappingSaved
}: DataSettingsDialogProps) => {
  const [view, setView] = useState<DataSettingsViewName>("hub");
  // What the settings looked like on entering a view, so Cancel can put them back.
  const [dataSettingSnapshot, setDataSettingSnapshot] =
    useState<DataSettings | null>(null);
  const [mappedAttribute, setMappedAttribute] = useState<string>();
  // A mapping from an earlier session, or one saved while this dialog is open.
  const isEditingAdmAreaMapping =
    hasSavedAdmAreaMapping || Boolean(mappedAttribute);

  // What the dataset actually holds: the state this dialog opened with, carried
  // forward each time a save lands. Closing rolls back to it, so an edit that was
  // never saved does not survive the dialog.
  const savedDataSetting = useRef(dataSetting);

  const handleClose = () => {
    setDataSetting(savedDataSetting.current);
    onClose();
  };

  const handleSelectCountry = (country: IOptionItem) => {
    const previousCountry = dataSetting?.country;
    setDataSetting((prev) => ({ ...prev, country }));
    onSaveCountry(country.value as string, {
      // The dataset has the country now, so Close shouldn't roll it back.
      onSaved: () => {
        savedDataSetting.current = { ...savedDataSetting.current, country };
      },
      onNotSaved: () =>
        setDataSetting((prev) => ({ ...prev, country: previousCountry }))
    });
  };

  const {
    selectedAttribute,
    outcome,
    isValidating,
    isConfirmingPartial,
    selectAttribute,
    resetMapping,
    mapAttribute,
    confirmPartialMapping
  } = useAdmAreaMapping({
    datasetId,
    isEditing: isEditingAdmAreaMapping,
    onMapped: (attributeId, fromPartial) => {
      setMappedAttribute(attributeId);
      // Rows were written: the mapping added a column and may have moved the
      // dataset's country, so the table and its metadata are stale. A partial
      // match that has not been confirmed never gets here, because nothing was
      // written yet.
      onMappingSaved();
      // Accepting a partial match ends the whole flow; every other save returns
      // to the hub so the user can keep configuring.
      if (fromPartial) {
        handleClose();
        return;
      }
      setView("hub");
    }
  });

  const handleDismiss = (event: Event) => {
    event.preventDefault();
    if (isSavingDataSetting || isValidating || isConfirmingPartial) return;
    handleClose();
  };

  const openView = (nextView: DataSettingsViewName) => {
    setDataSettingSnapshot(dataSetting);
    setView(nextView);
  };

  const handleCancelSpatialSettings = () => {
    setDataSetting(dataSettingSnapshot);
    setView("hub");
  };

  // The view holds until the save lands, so the hub and its success toast arrive
  // together. A failed save never moves the counter, so the user stays on the
  // view with what they entered rather than being dropped back with nothing
  // written.
  const lastSavedCount = useRef(dataSettingSavedCount);
  useEffect(() => {
    if (dataSettingSavedCount === lastSavedCount.current) return;
    lastSavedCount.current = dataSettingSavedCount;
    savedDataSetting.current = dataSetting;
    // The null-cells confirmation, when there is one, then sits over the hub —
    // the same place the old popup left it.
    setView("hub");
  }, [dataSettingSavedCount, dataSetting]);

  const handleCancelAdmAreaMapping = () => {
    resetMapping(mappedAttribute);
    setView("hub");
  };

  return (
    <DialogContent
      isHideClose
      className="!rounded-xl w-[500px]"
      overlayClassName={isBehindAnotherDialog ? "bg-transparent" : undefined}
      onEscapeKeyDown={handleDismiss}
      onInteractOutside={handleDismiss}
    >
      <DialogTitle className="hidden" />
      <DialogDescription className="sr-only">
        {DATA_SETTINGS_MESSAGE.DIALOG_DESCRIPTION}
      </DialogDescription>

      {view === "hub" && (
        <>
          <ModalHeader
            title={DATA_SETTINGS_MESSAGE.TITLE}
            className="p-0 m-0"
            onClose={handleClose}
          />
          <span className="text-xs text-dim-gray">
            {DATA_SETTINGS_MESSAGE.DESCRIPTION}
          </span>
          <DataSettingsHub
            countryId={countryId}
            dataSetting={dataSetting}
            spatialAttributeLabel={spatialAttributeLabel}
            admAreaAttribute={mappedAttribute}
            hasSavedAdmAreaMapping={hasSavedAdmAreaMapping}
            isSavingCountry={isSavingCountry}
            setDataSetting={setDataSetting}
            onSelectCountry={handleSelectCountry}
            onOpenSpatialSettings={() => openView("spatial")}
            onOpenAdmAreaMapping={() => openView("admArea")}
          />
          <Button
            className="h-[40px] w-full btn-primary text-sm !font-bold"
            onClick={handleClose}
            data-testid={`${TEST_ID_PREFIX}-close-button`}
          >
            <span>Close</span>
          </Button>
        </>
      )}

      {view === "spatial" && (
        <SpatialSettingsView
          type={type}
          dataSetting={dataSetting}
          attributesLatitude={attributesLatitude}
          attributesLongitude={attributesLongitude}
          attributesForAdminArea={attributesForAdminArea}
          isCoordinateRejected={isCoordinateRejected}
          setDataSetting={setDataSetting}
          onBack={handleCancelSpatialSettings}
          isSaving={isSavingDataSetting}
          onCancel={handleCancelSpatialSettings}
          onClose={handleClose}
          onSave={onSaveDataSetting}
        />
      )}

      {view === "admArea" && (
        <AdmAreaMappingView
          attributesForAdminArea={attributesForAdminArea}
          selectedAttribute={selectedAttribute}
          isEditing={isEditingAdmAreaMapping}
          outcome={outcome}
          isValidating={isValidating}
          isConfirmingPartial={isConfirmingPartial}
          onSelectAttribute={selectAttribute}
          onBack={handleCancelAdmAreaMapping}
          onCancel={handleCancelAdmAreaMapping}
          onClose={handleClose}
          onMap={mapAttribute}
          onConfirmPartial={confirmPartialMapping}
        />
      )}
    </DialogContent>
  );
};

export default DataSettingsDialog;
